import type { ApiPlayable } from "@hive/sdk";
import { hive } from "./apiClient";

const COLLECTION_PAGE_SIZE = 100;

/**
 * Hard stop for both drain loops below. Every signal that ends them is a value the
 * backend supplies, and the SDK's types only *claim* those are well-formed — they
 * are not validated at runtime. A `totalPages` the backend never filled in, or a
 * cursor that stops advancing, turns "read the rest" into an endless request storm
 * against the API with the tab pinned at 100% CPU.
 *
 * 50 pages is 5000 items, well past any real collection: reaching it means the
 * pagination contract broke, not that the viewer likes a lot of games.
 */
const MAX_COLLECTION_PAGES = 50;

function warnTruncated(collection: string) {
	if (import.meta.env.DEV) {
		console.warn(`${collection}: stopped at ${MAX_COLLECTION_PAGES} pages — pagination meta looks wrong`);
	}
}

export async function loadAllLikedPlayables(): Promise<ApiPlayable[]> {
	const items: ApiPlayable[] = [];
	let cursor: string | undefined;

	for (let page = 0; page < MAX_COLLECTION_PAGES; page += 1) {
		const result = await hive.me.likes({ limit: COLLECTION_PAGE_SIZE, cursor });
		items.push(...result.items);
		const next = result.meta?.nextCursor;
		// A cursor that comes back unchanged would replay the same page forever.
		if (!result.meta?.hasMore || !next || next === cursor) return items;
		cursor = next;
	}
	warnTruncated("likes");
	return items;
}

async function loadAllFavoritePlayables(): Promise<ApiPlayable[]> {
	const items: ApiPlayable[] = [];

	for (let pageNumber = 1; pageNumber <= MAX_COLLECTION_PAGES; pageNumber += 1) {
		const page = await hive.me.favorites({ page: pageNumber, pageSize: COLLECTION_PAGE_SIZE });
		items.push(...page.items);
		// Fail closed on a total the backend did not send: `pageNumber >= undefined` is
		// false forever, so the missing field asks for every page there has ever been.
		// Stopping one page early is recoverable; not stopping is not.
		const totalPages = Number(page.meta?.totalPages);
		if (!Number.isFinite(totalPages) || pageNumber >= totalPages) return items;
	}
	warnTruncated("favorites");
	return items;
}

export async function loadViewerInteractionIds(): Promise<[string[], string[]]> {
	const [liked, favorited] = await Promise.all([
		loadAllLikedPlayables().catch(() => [] as ApiPlayable[]),
		loadAllFavoritePlayables().catch(() => [] as ApiPlayable[]),
	]);
	return [
		liked.map((item) => item.id),
		favorited.map((item) => item.id),
	];
}
